import { InfiniteData, QueryClient } from '@tanstack/react-query';
import { OrderEntity } from 'src/entities/orderEntity';

export class PlanningsOrdersCache {
  constructor(private queryClient: QueryClient) {}

  addOrders(orderEntities: OrderEntity[]): void {
    const orderCache = this.queryClient.getQueriesData({ queryKey: ['orders'] });
    orderCache.forEach(([queryCacheKey, queryData]) => {
      if (queryData) {
        if (typeof queryData === 'object' && 'pages' in queryData) {
          const cacheStartAt = new Date(queryCacheKey[1] as string);
          cacheStartAt.setHours(0, 0, 0, 0);
          const cacheEndAt = new Date(queryCacheKey[2] as string);
          cacheEndAt.setHours(23, 59, 59, 999);
          const ordersToAdd = orderEntities.filter((it) => {
            const orderStartAt = new Date(it.loading_start_at);
            return cacheStartAt.getTime() <= orderStartAt.getTime() && orderStartAt.getTime() <= cacheEndAt.getTime();
          });
          const cacheData = queryData as InfiniteData<{
            ids: number[],
            data: OrderEntity[],
            total_pages_count: number;
          }>;
          const newPages = cacheData.pages.map((page, idx) => {
            if (idx === (cacheData.pages.length - 1)) {
              return {
                ...page,
                ids: [...page.ids, ...ordersToAdd.map((it) => it.id)],
                data: [...page.data, ...ordersToAdd],
              };
            }
            return page;
          });

          this.queryClient.setQueryData(queryCacheKey, { ...cacheData, pages: newPages });
        }
      }
    });
  }

  update(orders: OrderEntity[]): void {
    const orderCache = this.queryClient.getQueriesData({ queryKey: ['orders'] });
    const orderIds = orders.map((it) => it.id);
    orderCache.forEach(([queryCacheKey, queryData]) => {
      if (queryData) {
        if (typeof queryData === 'object' && 'id' in queryData) {
          const orderEntity = queryData as OrderEntity;
          if (orderIds.includes(orderEntity.id)) {
            this.queryClient.setQueryData(queryCacheKey, orders.find((it) => it.id === orderEntity.id));
          }
        } else if (typeof queryData === 'object' && 'pages' in queryData) {
          const cacheData = queryData as InfiniteData<{
            ids: number[],
            data: OrderEntity[],
            total_pages_count: number;
          }>;
          const newPages = cacheData.pages.map((page) => ({
            ...page,
            data: page.data.map((it) => {
              if (orderIds.includes(it.id)) {
                return orders.find((o) => o.id === it.id);
              }
              return it;
            }),
          }));
          this.queryClient.setQueryData(queryCacheKey, { ...cacheData, pages: newPages });
        }
      }
    });
  }

  updateSiblingsAreAllocated(orderEntities: OrderEntity[], orderIdsHasDeliveries: number[]): void {
    const splittedOrders = orderEntities.filter((it) => it.was_split);
    if (splittedOrders.length > 0) {
      const splittedOrderIds = splittedOrders.map((it) => it.id);

      const orderCache = this.queryClient.getQueriesData({ queryKey: ['orders'] });
      orderCache.forEach(([queryCacheKey, queryData]) => {
        if (queryData) {
          if (typeof queryData === 'object' && 'id' in queryData) {
            const orderEntity = queryData as OrderEntity;
            if (splittedOrderIds.includes(orderEntity.id)) {
              this.queryClient.removeQueries({ queryKey: queryCacheKey });
            }
          } else if (typeof queryData === 'object' && 'pages' in queryData) {
            const cacheData = queryData as InfiniteData<{
              ids: number[],
              data: OrderEntity[],
              total_pages_count: number;
            }>;
            const newPages = cacheData.pages.map((page) => ({
              ...page,
              data: page.data.map((it) => {
                if (splittedOrderIds.includes(it.id)) {
                  return {
                    ...it,
                    siblings_are_allocated: it.sibling_order_ids.some((id) => orderIdsHasDeliveries.includes(id)),
                  };
                }
                return it;
              })
            }));
            this.queryClient.setQueryData(queryCacheKey, { ...cacheData, pages: newPages });
          }
        }
      });
    }
  }

  removeOrderByIds(orderIds: number[]): void {
    const orderCache = this.queryClient.getQueriesData({ queryKey: ['orders'] });
    orderCache.forEach(([queryCacheKey, queryData]) => {
      if (queryData) {
        if (typeof queryData === 'object' && 'id' in queryData) {
          const orderEntity = queryData as OrderEntity;
          if (orderIds.includes(orderEntity.id)) {
            this.queryClient.removeQueries({ queryKey: queryCacheKey });
          }
        } else if (typeof queryData === 'object' && 'pages' in queryData) {
          const cacheData = queryData as InfiniteData<{
            ids: number[],
            data: OrderEntity[],
            total_pages_count: number;
          }>;
          const newPages = cacheData.pages.map((page) => ({
            ...page,
            ids: page.ids.filter((id) => !orderIds.includes(id)),
            data: page.data.filter((dt) => !orderIds.includes(dt.id)),
          }));
          this.queryClient.setQueryData(queryCacheKey, { ...cacheData, pages: newPages });
        }
      }
    });
  }

  removeAssignments(orderIds: number[]): void {
    const orderCache = this.queryClient.getQueriesData({ queryKey: ['orders'] });
    orderCache.forEach(([queryCacheKey, cacheData]) => {
      if (typeof cacheData === 'object' && 'id' in cacheData) {
        const orderEntity = cacheData as OrderEntity;
        if (orderIds.includes(orderEntity.id)) {
          const newEntity = {
            ...orderEntity,
            order_operations: [],
            order_assignment_attributes: undefined,
            order_operations_count: 0,
          };
          this.queryClient.setQueryData(queryCacheKey, newEntity);
        }
      } else if (cacheData && typeof cacheData === 'object' && 'pages' in cacheData) {
        const paginatedData = cacheData as InfiniteData<{
          ids: number[];
          total_pages_count: number;
          data: OrderEntity[];
        }>;
        const newPages = paginatedData.pages.map((page) => {
          const data = page.data.map((dt) => {
            if (orderIds.includes(dt.id)) {
              return {
                ...dt,
                order_operations: [],
                order_assignment_attributes: undefined,
                order_operations_count: 0,
              };
            }
            return dt;
          });
          return {
            ...page,
            ids: page.ids.filter((it) => !orderIds.includes(it)),
            data,
          };
        });
        this.queryClient.setQueryData(queryCacheKey, { ...paginatedData, pages: newPages });
      }
    });
  }
}
